import React from 'react';
import I18n from 'i18n-js';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import { flowRight } from 'lodash';

import { IAddressFormData } from './addresses/address_form_data';
import { CountrySelector } from './discovery/country_selector';
import { ICountry } from '../schemas/core-schema';
import { ReverbAddressFormFieldsConfigsQuery } from '../schemas/rql';
import AddressFormField from './address_form_field';
import { buildInputName, configFields } from '../lib/address_form_fields_helpers';
import {
  AddressInputField,
  LabelOverride,
  Field,
} from '../lib/address_form_fields_types';

import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

interface Props {
  address: IAddressFormData;
  countries: ICountry[];
  updateField: (attributes) => void;
  fieldPrefix?: string;
  requiredFieldsOverrides?: AddressInputField[];
  hiddenFieldsOverrides?: AddressInputField[];
  disabledFieldsOverrides?: AddressInputField[];
  labelOverrides?: LabelOverride;
  phoneLabel?: JSX.Element | React.ReactNode;
  handleUpdateField?: boolean;
}

export function AddressFormFields(props: ChildProps<Props, ReverbAddressFormFieldsConfigsQuery.Query>) {
  const { updateField, address, countries, handleUpdateField, data } = props;
  const { loading, intlAddressFormConfig } = data;
  const user = useUser();

  if (loading || !intlAddressFormConfig) return null;

  const fieldPrefix = props.fieldPrefix || '';
  const requiredFieldsOverrides = props.requiredFieldsOverrides || [];
  const hiddenFieldsOverrides = props.hiddenFieldsOverrides || [];
  const disabledFieldsOverrides = props.disabledFieldsOverrides || [];
  const labelOverrides = props.labelOverrides || {};

  const configs = intlAddressFormConfig?.configs;
  const addressFields = configFields(address.countryCode, configs);

  return (
    <div data-address-fields>
      <FormGroup label={I18n.t('discovery.addressFormFields.country')}>
        <CountrySelector
          inputName={buildInputName(fieldPrefix, 'country_code')}
          value={address.countryCode}
          onChange={e => handleCountryChange(e, addressFields, address, handleUpdateField, updateField, configs)}
          countries={countries}
          disabled={disabledFieldsOverrides.includes('country')}
        />
      </FormGroup>

      {filterHidden(addressFields, hiddenFieldsOverrides).map((field) => (
        <AddressFormField
          key={field.fieldName}
          field={field}
          user={user}
          address={address}
          countries={countries}
          handleUpdateField={handleUpdateField}
          updateField={updateField}
          requiredFieldsOverrides={requiredFieldsOverrides}
          labelOverrides={labelOverrides}
          fieldPrefix={fieldPrefix}
          phoneLabel={props.phoneLabel}
        />
      ))}
    </div>
  );
}

function filterHidden(fields: Field[], hiddenFieldsOverrides: AddressInputField[]): Field[] {
  return fields.filter(field => !hiddenFieldsOverrides.includes(field.fieldName));
}

function handleCountryChange(evt, currentFields, address, handleUpdateField, updateField, configs) {
  const fieldsFromConfig = configFields(evt, configs);

  // sets all fields not in new form to blank strings
  const newFields = fieldsFromConfig.map((field) => field.fieldName);
  const oldFields = currentFields.map((field) => field.fieldName);
  const fieldsDifference = oldFields.filter((oldField) => !newFields.includes(oldField));
  const blankFields = fieldsDifference.reduce((prev, fieldName) => ({ ...prev, [fieldName]: '' }), {});
  const newAddressState = { ...address, ...camelizeKeys(blankFields), countryCode: evt };

  if (handleUpdateField) {
    updateField(newAddressState);
  } else {
    updateField(decamelizeKeys(newAddressState));
  }
}

const ADDRESS_FORM_FIELDS_CONFIGS_QUERY = gql`
  query Reverb_Address_Form_Fields_Configs_Query {
    intlAddressFormConfig {
      configs {
        countryCode
        fields {
          fieldName
          displayNames {
            locale
            displayName
          }
          required
        }
      }
    }
  }
`;

const connect = graphql<Props, ReverbAddressFormFieldsConfigsQuery.Query>(
  ADDRESS_FORM_FIELDS_CONFIGS_QUERY,
);

export default flowRight([connect])(AddressFormFields);
